/**
 * Generic image-file attacher for forms with a hidden <input type="file">.
 *
 * Used by the feedback form and the suspension-appeal form. Both share the
 * same flow: drag-or-click image upload, in-memory preview thumbs, with
 * client-side limits on count, file type, and size.
 *
 * Translations and limits come from a data-config JSON attribute on the
 * Alpine host element so this module stays free of trans() calls and can
 * be bundled by webpack/JIT without any Twig interpolation.
 *
 * Usage in Twig:
 *
 *   <div x-data="fileAttacher"
 *        data-config='{
 *            "maxFiles": 3,
 *            "inputRef": "feedbackFileInput",
 *            "messages": {
 *                "tooMany":   "{{ trans(...) }}",
 *                "badType":   "{{ trans(...) }}",
 *                "tooLarge":  "{{ trans(...) }}"
 *            }
 *        }'>
 *      ...
 *      <input type="file" x-ref="feedbackFileInput" @change="addFiles($event)" ...>
 *   </div>
 */

const DEFAULT_MAX_FILES = 3;
const MAX_BYTES_PER_FILE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export function fileAttacher() {
    return {
        files: [],
        dragOver: false,
        errorMsg: '',
        maxFiles: DEFAULT_MAX_FILES,
        inputRef: '',
        messages: {
            tooMany: 'Too many attachments.',
            badType: 'Unsupported file type.',
            tooLarge: 'File is too large.',
        },

        init() {
            const raw = this.$root?.dataset?.config;
            if (!raw) {
                return;
            }
            try {
                const config = JSON.parse(raw);
                if (Number.isInteger(config.maxFiles) && config.maxFiles > 0) {
                    this.maxFiles = config.maxFiles;
                }
                if (typeof config.inputRef === 'string' && config.inputRef) {
                    this.inputRef = config.inputRef;
                }
                if (config.messages && typeof config.messages === 'object') {
                    this.messages = { ...this.messages, ...config.messages };
                }
            } catch (_e) {
                // Bad JSON in data-config: leave defaults so the form still works.
            }
        },

        addFiles(event) {
            const input = event.target;
            if (!input.files) {
                return;
            }
            this.processFiles(input.files);
            input.value = '';
        },

        handleDrop(event) {
            this.dragOver = false;
            if (event.dataTransfer && event.dataTransfer.files) {
                this.processFiles(event.dataTransfer.files);
            }
        },

        processFiles(fileList) {
            this.errorMsg = '';

            Array.from(fileList).forEach((file) => {
                if (this.files.length >= this.maxFiles) {
                    this.errorMsg = this.messages.tooMany;
                    return;
                }
                if (!ALLOWED_TYPES.includes(file.type)) {
                    this.errorMsg = this.messages.badType;
                    return;
                }
                if (file.size > MAX_BYTES_PER_FILE) {
                    this.errorMsg = this.messages.tooLarge;
                    return;
                }
                this.files.push({
                    file,
                    url: URL.createObjectURL(file),
                });
            });

            this.syncFileInput();
        },

        removeFile(index) {
            const removed = this.files.splice(index, 1);
            if (removed.length > 0) {
                URL.revokeObjectURL(removed[0].url);
            }
            this.syncFileInput();
        },

        syncFileInput() {
            const ref = this.inputRef;
            if (!ref || !this.$refs[ref]) {
                return;
            }
            const dt = new DataTransfer();
            this.files.forEach((entry) => {
                dt.items.add(entry.file);
            });
            this.$refs[ref].files = dt.files;
        },
    };
}
